import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { waitFor, modifyProps } from 'lp-hoc'
import { ExperienceSkillsForm } from '../forms'
import * as apiActions from 'api-actions'
import * as Types from 'types'
import { selectors as globalArtistSelectors } from 'artist-portal-reducer'
import * as globalActions from 'artist-portal-actions'
import * as flashActions from 'redux-flash'
import {
  yankBy,
  isOtherType,
  displaySubmitFailure,
  getOtherTypeId,
  useCommunity,
} from 'utils'
import { selectors as globalSelectors } from 'reducer'
import { get } from 'lodash'
import syncProfileCompletion from '../sync-profile-completion'

const propTypes = {
  artist: Types.artist.isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
  currentArtistLocation: Types.location.isRequired,
  setArtist: PropTypes.func.isRequired,
  updateArtist: PropTypes.func.isRequired,
  approaches: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  outcomeTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  programTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  disciplines: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  languages: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
}

const defaultProps = {
  disciplines: [],
}

function ExperienceSkills({
  flashSuccessMessage,
  flashErrorMessage,
  currentArtistLocation,
  updateArtist,
  setArtist,
  artist,
  approaches,
  disciplines,
  outcomeTypes,
  programTypes,
  languages,
}) {
  const community = useCommunity()

  const skills = artist ? artist.skills : {}
  const [otherApproach, definedApproaches] = useMemo(() => {
    const approaches = get(skills, 'approaches', [])
    return yankBy(approaches, isOtherType)
  }, [skills])

  const [otherOutcome, definedOutcomes] = useMemo(() => {
    const outcomes = get(skills, 'outcomes', [])
    return yankBy(outcomes, isOtherType)
  }, [skills])

  const [otherLanguage, definedLanguages] = useMemo(() => {
    const languages = get(skills, 'languages', [])
    return yankBy(languages, isOtherType)
  }, [skills])

  return (
    <ExperienceSkillsForm
      name={`aritst-experience`}
      initialValues={{
        ...artist,
        approaches: definedApproaches,
        otherApproach: {
          ...otherApproach,
          approachId: getOtherTypeId(approaches),
        },
        languages: definedLanguages,
        otherLanguage: {
          ...otherLanguage,
          languageId: getOtherTypeId(languages),
        },
        outcomes: definedOutcomes,
        otherOutcome: {
          ...otherOutcome,
          outcomeId: getOtherTypeId(outcomeTypes),
        },
        subDisciplines: get(skills, 'subDisciplines', []),
        programTypes: get(skills, 'programTypes', []),
      }}
      onSubmit={updateArtist}
      onSubmitSuccess={(updatedArtist) => {
        setArtist(updatedArtist)
        syncProfileCompletion({
          artist: updatedArtist,
          previousArtist: artist,
          community,
          updateArtistBadge: updateArtist,
        })
        flashSuccessMessage('Artist information updated!')
      }}
      onSubmitFail={displaySubmitFailure}
      currentArtistLocation={currentArtistLocation}
      flashErrorMessage={flashErrorMessage}
      flashSuccessMessage={flashSuccessMessage}
      artistId={artist.id}
      approachTypes={approaches}
      disciplines={disciplines}
      outcomeTypes={outcomeTypes}
      programTypes={programTypes}
      languages={languages}
    />
  )
}

ExperienceSkills.propTypes = propTypes
ExperienceSkills.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    artist: globalArtistSelectors.artist(state),
    currentArtistLocation: globalArtistSelectors.currentArtistLocation(state),
    approaches: globalSelectors.approaches(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    disciplines: globalSelectors.definedDisciplines(state),
    outcomeTypes: globalSelectors.outcomeTypes(state),
    programTypes: globalSelectors.programTypes(state),
    languages: globalSelectors.languages(state),
  }
}

const mapDispatchToProps = {
  flashSuccessMessage: flashActions.flashSuccessMessage,
  flashErrorMessage: flashActions.flashErrorMessage,
  setArtist: globalActions.setArtist,
  updateArtist: apiActions.updateArtist,
}

function modify({ artist, currentSchoolYear, updateArtist }) {
  return {
    updateArtist: (params) =>
      updateArtist(artist.id, currentSchoolYear.id, params),
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  waitFor(['artist', 'approaches', 'disciplines', 'outcomeTypes', 'languages']),
  modifyProps(modify)
)(ExperienceSkills)
